/**
 * Slideshow — image carousel for the expanded solution panel.
 *
 * Usage:
 *   <div class="ss-player" data-images='["path/a.jpg","path/b.jpg"]'
 *        data-autoplay="3500">
 *     <img class="ss-img" src="" alt="">
 *     <div class="ss-controls">
 *       <button class="ss-btn ss-prev">&#8249;</button>
 *       <span class="ss-counter"></span>
 *       <button class="ss-btn ss-next">&#8250;</button>
 *     </div>
 *   </div>
 *
 * data-autoplay — interval in ms (omit to disable autoplay)
 * Listens for sc:panelopen so it initialises images injected via <template>.
 */

(function (): void {
    function initSlideshow(player: HTMLElement): void {
        const raw = player.dataset.images;
        if (!raw) return;

        let images: string[];
        try { images = JSON.parse(raw); } catch { return; }
        if (!images.length) return;

        player.setAttribute('data-ss-ready', '');

        const img     = player.querySelector<HTMLImageElement>('.ss-img')!;
        const counter = player.querySelector<HTMLElement>('.ss-counter')!;
        const prevBtn = player.querySelector<HTMLButtonElement>('.ss-prev')!;
        const nextBtn = player.querySelector<HTMLButtonElement>('.ss-next')!;

        const autoplayMs = player.dataset.autoplay ? parseInt(player.dataset.autoplay, 10) : 0;
        let timer: ReturnType<typeof setInterval> | null = null;
        let current = 0;

        function show(idx: number): void {
            img.style.opacity = '0';
            setTimeout(() => {
                current = ((idx % images.length) + images.length) % images.length;
                img.src = encodeURI(images[current]);
                counter.textContent = `${current + 1} / ${images.length}`;
                img.style.opacity = '1';
            }, 280);
        }

        function startAutoplay(): void {
            if (!autoplayMs) return;
            timer = setInterval(() => show(current + 1), autoplayMs);
        }

        function resetAutoplay(): void {
            if (!autoplayMs) return;
            if (timer) clearInterval(timer);
            startAutoplay();
        }

        // Load first image immediately
        img.src = encodeURI(images[0]);
        counter.textContent = `1 / ${images.length}`;
        startAutoplay();

        prevBtn.addEventListener('click', () => { show(current - 1); resetAutoplay(); });
        nextBtn.addEventListener('click', () => { show(current + 1); resetAutoplay(); });

        // Pause on hover, resume on leave
        player.addEventListener('mouseenter', () => { if (timer) clearInterval(timer); });
        player.addEventListener('mouseleave', () => startAutoplay());

        // Keyboard navigation when panel is open
        document.addEventListener('keydown', function (e: KeyboardEvent) {
            const panel = document.getElementById('sc-panel');
            if (!panel?.classList.contains('is-open')) return;
            if (e.key === 'ArrowLeft')  { show(current - 1); resetAutoplay(); }
            if (e.key === 'ArrowRight') { show(current + 1); resetAutoplay(); }
        });

        // Stop autoplay when panel closes
        document.getElementById('sc-panel') &&
            new MutationObserver(() => {
                const panel = document.getElementById('sc-panel');
                if (!panel?.classList.contains('is-open') && timer) {
                    clearInterval(timer);
                    timer = null;
                }
            }).observe(document.getElementById('sc-panel')!, {
                attributes: true, attributeFilter: ['class'],
            });
    }

    function initAll(): void {
        document.querySelectorAll<HTMLElement>('.ss-player:not([data-ss-ready])')
            .forEach(initSlideshow);
    }

    document.addEventListener('DOMContentLoaded', initAll);
    document.addEventListener('sc:panelopen', function () {
        requestAnimationFrame(initAll);
    });
})();
